/**
 * PanelPlanningPage(mixin 조합) → MapHost 플러그인 ctx 브릿지
 * @param {object} vm — Vue 컴포넌트 인스턴스 (this)
 */
export function createPanelPlanningMapPluginContext(vm) {
  return {
    getTargetElement: () => document.getElementById(vm.mapHost?.targetId || 'map'),
    getVectorLayer: () => vm.vectorLayer,
    getVectorSource: () => vm.vectorSource,
    getMap: () => vm.map,

    getDrawingActive: () => Boolean(vm.drawingToolbar?.activeTool),

    initSelectionCollections: () => {
      if (typeof vm.initPanelMapSelectionCollections === 'function') {
        vm.initPanelMapSelectionCollections();
      }
    },
    getGroupSelection: () => vm.groupSelection,
    getModuleSelection: () => vm.moduleSelection,
    assignInteractions: refs => {
      if (refs.groupSelection) {
        vm.groupSelection = refs.groupSelection;
      }
      if (refs.moduleSelection) {
        vm.moduleSelection = refs.moduleSelection;
      }
      vm.modifyInteraction = refs.modifyInteraction;
      vm.translateInteraction = refs.translateInteraction;
      vm.dragPanInteraction = refs.dragPanInteraction;
    },

    getSuppressMapSelectionUntil: () => vm._suppressMapSelectionUntil || 0,
    isSelectionBlocked: () =>
      Boolean(vm.isPasteMode || vm.drawingToolbar?.activeTool || vm.divisionPopupOpen),
    isDrawingActive: () => Boolean(vm.drawingToolbar?.activeTool),
    isCtrlPressed: () => Boolean(vm.ctrlKeyPressed),
    setDragPanActive: active => {
      vm.dragPanInteraction?.setActive(active);
    },
    setTranslateInteractionActive: active => {
      if (vm.translateInteraction) {
        vm.translateInteraction.setActive(active);
      }
    },
    isCtrlDragActive: () => Boolean(vm._mapCtrlDragActive),
    getMapClickTargetFeatureAtPixel: pixel => vm.getMapClickTargetFeatureAtPixel(pixel),
    removeSelectionHighlight: () => vm.removeSelectionHighlight?.(),
    onGroupSingleClick: (hit, opts) => vm.handleGroupMapSingleClick(hit, opts),
    onModuleDblClick: hit => vm.handleModuleMapDblClick(hit),
    onClearSelection: () => vm.clearAllMapSelection(),

    setCtrlKeyPressed: value => {
      vm.ctrlKeyPressed = value;
    },
    copySelectedFeature: () => vm.copySelectedFeature(),
    activatePasteMode: () => vm.activatePasteMode(),
    deactivatePasteMode: () => vm.deactivatePasteMode(),
    isPasteMode: () => Boolean(vm.isPasteMode),
    getCopiedFeature: () => vm.copiedFeature,
    pasteFeatureAtCoordinate: coord => vm.pasteFeatureAtCoordinate(coord),

    resolveCtrlDragSourceFeature: hit => vm.resolveCtrlDragSourceFeature(hit),
    getSelectedStringFeaturesForCtrlDrag: hit => vm.getSelectedStringFeaturesForCtrlDrag(hit),
    resolveSourceStringRowCol: original => vm.resolveSourceStringRowCol(original),
    resolveCtrlDragCopySkipSlots: original => vm.resolveCtrlDragCopySkipSlots(original),
    processMultiStringCtrlDragDrop: items => vm.processMultiStringCtrlDragDrop(items),
    suspendMainMapInteractionsForCtrlDrag: () => vm.suspendMainMapInteractionsForCtrlDrag(),
    restoreMainMapInteractionsAfterCtrlDrag: () => vm.restoreMainMapInteractionsAfterCtrlDrag(),
    teardownCtrlDragTranslate: (state, opts) => vm.teardownCtrlDragTranslate(state, opts),
    finishStringCtrlDragAsDivision: state => vm.finishStringCtrlDragAsDivision(state),
    createCtrlDragModuleClones: source => vm.createCtrlDragModuleClones(source),
    extractFeatureData: feature => vm.extractFeatureData(feature),
    setCopiedFeature: (data, type) => {
      vm.copiedFeature = data;
      vm.copiedFeatureType = type;
    },
    setCtrlDragActive: value => {
      vm._mapCtrlDragActive = value;
    },

    // Ctrl+드래그 → 강제 auto-draw (모달 없이 바로 저장)
    forceAutoDraw: async fn => {
      const prev = vm.autoDrawEnabled;
      vm.autoDrawEnabled = true;
      try {
        await fn();
      } finally {
        if (!prev) vm.autoDrawEnabled = false;
      }
    },

    // Ctrl+드래그 — API rowNum/colNum → moduleRowNum/moduleColNum 후 divide
    setDrawnPolygonFromApiRowCol: (feature, rowNum, colNum) => {
      vm.drawnPolygon = feature;
      vm.applyApiRowColToModuleColRow?.(rowNum, colNum);
    },
    applyApiRowColToModuleColRow: (rowNum, colNum) =>
      vm.applyApiRowColToModuleColRow(rowNum, colNum),
    processDrawnPolygon: feature => vm.processDrawnPolygon(feature),
    confirmDivision: options => vm.confirmDivision(options),
    cancelDivision: () => vm.cancelDivision(),
    dividePolygon: options => vm.dividePolygon(options),

    resolveStringPlacementArraySeqForDrop: opts => vm.resolveStringPlacementArraySeqForDrop(opts),
    updateStringPlacementPreview: opts => vm.updateStringPlacementPreview(opts),
    clearStringPlacementPreview: () => vm.clearStringPlacementPreview(),
    restoreMapUiAfterInteractionCancel: () => vm.restoreMapUiAfterInteractionCancel?.(),
    registerMapInteractionCancelHandlers: handlers =>
      vm.registerMapInteractionCancelHandlers?.(handlers),
    syncStringDrawTargetFromMapFeature: feature => vm.syncStringDrawTargetFromMapFeature(feature),
    syncStringDrawTargetFromArraySeq: seq => vm.syncStringDrawTargetFromArraySeq(seq),
    warnStringDrawTargetArrayRequired: () => vm.warnStringDrawTargetArrayRequired(),

    // 그리기
    startDrawing: () => vm.startDrawing(),
    exitDrawingMode: () => vm.exitDrawingMode(),
    setDrawingTool: tool => {
      if (vm.drawingToolbar) vm.drawingToolbar.activeTool = tool;
    },
    getDrawnPolygon: () => vm.drawnPolygon,
    getModuleColNum: () => vm.moduleColNum,
    getModuleRowNum: () => vm.moduleRowNum,
    setModuleColNum: v => {
      vm.moduleColNum = v;
    },
    setModuleRowNum: v => {
      vm.moduleRowNum = v;
    },

    // Feature 그리기
    convToFeatureWithHierarchy: (id, name, coords, style, hierarchy) =>
      vm.convToFeatureWithHierarchy(id, name, coords, style, hierarchy)
  };
}
